/*
 * Purple Carrot - mobile header search bar
 * Self-contained predictive search for the mobile header.
 * Deliberately does NOT reuse the theme's <predictive-search> element:
 * that class resolves most of its selectors against the single global
 * [data-search-popup] node, so a second instance would fight with the
 * search popup. This element owns its own DOM and its own fetch.
 */
(function () {
  if (typeof customElements === 'undefined') return;
  if (customElements.get('m-mobile-search')) return;

  var DEBOUNCE = 300;

  class MMobileSearch extends HTMLElement {
    constructor() {
      super();
      this.cache = {};
      this.timer = null;
      this.abort = null;
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-mobile-search-input]');
      this.results = this.querySelector('[data-mobile-search-result]');
      this.resultsInner = this.querySelector('[data-mobile-predictive-search]');
      this.spinner = this.querySelector('[data-mobile-search-spinner]');
      this.clearBtn = this.querySelector('[data-mobile-search-clear]');
      this.predictive = this.dataset.predictive === 'true';

      if (!this.form || !this.input) return;

      this._onInput = this.onInput.bind(this);
      this._onFocus = this.onFocus.bind(this);
      this._onSubmit = this.onSubmit.bind(this);
      this._onClear = this.onClear.bind(this);
      this._onDocClick = this.onDocClick.bind(this);
      this._onKeydown = this.onKeydown.bind(this);

      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('focus', this._onFocus);
      this.form.addEventListener('submit', this._onSubmit);
      if (this.clearBtn) this.clearBtn.addEventListener('click', this._onClear);
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKeydown);
    }

    getQuery() {
      return this.input.value.trim();
    }

    onInput() {
      var query = this.getQuery();
      this.toggleClear(query.length > 0);

      if (!this.predictive) return;

      clearTimeout(this.timer);
      if (query.length < 2) {
        this.close();
        this.toggleSpinner(false);
        return;
      }
      this.timer = setTimeout(this.search.bind(this, query), DEBOUNCE);
    }

    onFocus() {
      if (this.predictive && this.getQuery().length >= 2 && this.resultsInner.innerHTML.trim()) {
        this.open();
      }
    }

    onSubmit(event) {
      if (!this.getQuery().length) event.preventDefault();
    }

    onClear(event) {
      event.preventDefault();
      this.input.value = '';
      this.toggleClear(false);
      this.close();
      this.input.focus();
    }

    onDocClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.close();
        this.input.blur();
      }
    }

    searchUrl(query) {
      var settings = (window.MinimogSettings && window.MinimogSettings.routes) || {};
      var base = settings.predictive_search_url || '/search/suggest';
      var fields = 'title,product_type,vendor,variants.sku,variants.title';
      if (this.dataset.searchByTag === 'true') fields += ',tag';
      if (this.dataset.searchByBody === 'true') fields += ',body';
      var unavailable = this.dataset.unavailableProductsOption || 'last';

      return (
        base +
        '?q=' + encodeURIComponent(query) +
        '&resources[options][unavailable_products]=' + unavailable +
        '&resources[options][fields]=' + fields +
        '&section_id=predictive-search'
      );
    }

    search(query) {
      var key = query.toLowerCase();
      if (this.cache[key]) {
        this.render(this.cache[key]);
        return;
      }

      this.toggleSpinner(true);
      if (this.abort) this.abort.abort();
      this.abort = typeof AbortController !== 'undefined' ? new AbortController() : null;

      fetch(this.searchUrl(query), this.abort ? { signal: this.abort.signal } : undefined)
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(
          function (text) {
            var section = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('#shopify-section-predictive-search');
            var markup = section ? section.innerHTML : '';
            this.cache[key] = markup;
            this.render(markup);
          }.bind(this)
        )
        .catch(
          function (error) {
            if (error && error.name === 'AbortError') return;
            this.toggleSpinner(false);
            this.close();
          }.bind(this)
        );
    }

    render(markup) {
      this.toggleSpinner(false);
      if (!this.resultsInner) return;
      this.resultsInner.innerHTML = markup;

      var wrapper = this.resultsInner.querySelector('[data-search-items-wrapper]');
      if (!markup.trim() || (wrapper && wrapper.childElementCount === 0)) {
        // Still open it: the fetched section renders its own "no results" state.
        this.open();
        return;
      }
      this.open();
    }

    open() {
      if (!this.results) return;
      this.setMaxHeight();
      this.results.classList.remove('m:hidden');
      this.setAttribute('open', 'true');
    }

    close() {
      if (!this.results) return;
      this.results.classList.add('m:hidden');
      this.removeAttribute('open');
    }

    setMaxHeight() {
      var rect = this.form.getBoundingClientRect();
      var available = window.innerHeight - rect.bottom - 24;
      this.results.style.setProperty('--search-result-max-height', Math.max(180, available) + 'px');
    }

    toggleSpinner(show) {
      if (!this.spinner) return;
      this.spinner.style.visibility = show ? 'visible' : 'hidden';
    }

    toggleClear(show) {
      if (!this.clearBtn) return;
      this.clearBtn.style.visibility = show ? 'visible' : 'hidden';
    }
  }

  customElements.define('m-mobile-search', MMobileSearch);
})();
